/**
 * Hook: usePrerequisites
 *
 * Consulta o estado dos elos de uma cadeia de pré-requisitos, e só quando a
 * listagem já voltou vazia — lista com dados nunca chama o endpoint (#385).
 *
 * Falha, demora ou resposta pendente devolvem `unavailable`: o estado vazio
 * existe sem a cadeia, e nunca troca o texto por um spinner.
 */

import { useEffect, useState } from 'react';
import { useChaveDoContexto } from '../stores/chaveDoContexto';
import { prerequisiteService, PrerequisiteNodes } from '../services/prerequisiteService';

export type PrerequisiteState =
  | { status: 'unavailable' }
  | { status: 'ready'; nodes: PrerequisiteNodes };

const UNAVAILABLE: PrerequisiteState = { status: 'unavailable' };

// Cache de sessão: navegar entre telas da mesma cadeia não repete a consulta.
// Guarda o contexto a que pertence, porque trocar de contexto muda todas as respostas.
const cache = new Map<string, PrerequisiteNodes>();
let cacheContexto: string | null = null;

function readCache(contexto: string | null, key: string): PrerequisiteNodes | undefined {
  if (cacheContexto !== contexto) return undefined;
  return cache.get(key);
}

function writeCache(contexto: string | null, key: string, nodes: PrerequisiteNodes): void {
  if (cacheContexto !== contexto) {
    cache.clear();
    cacheContexto = contexto;
  }
  cache.set(key, nodes);
}

/** O cache é de módulo e sobrevive entre renders — os testes precisam zerá-lo. */
export function clearPrerequisiteCache(): void {
  cache.clear();
  cacheContexto = null;
}

export function usePrerequisites(nodes: string[], enabled: boolean): PrerequisiteState {
  const contexto = useChaveDoContexto();
  const [state, setState] = useState<PrerequisiteState>(UNAVAILABLE);

  // A lista de elos é recriada a cada render; o que identifica a consulta é o conteúdo.
  const nodesKey = nodes.join(',');

  useEffect(() => {
    if (!enabled || nodesKey === '') {
      setState(UNAVAILABLE);
      return;
    }

    const cached = readCache(contexto, nodesKey);
    if (cached) {
      setState({ status: 'ready', nodes: cached });
      return;
    }

    let active = true;
    setState(UNAVAILABLE);

    prerequisiteService
      .getPrerequisites(nodesKey.split(','))
      .then((result) => {
        writeCache(contexto, nodesKey, result);
        if (active) setState({ status: 'ready', nodes: result });
      })
      .catch(() => {
        if (active) setState(UNAVAILABLE);
      });

    return () => {
      active = false;
    };
  }, [enabled, nodesKey, contexto]);

  return state;
}

export default usePrerequisites;
